import {
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ratingApi } from 'apis/rating';
import { Language, languages } from 'app/utils/supportedLanguages';
import { AppLink } from 'components/appLink';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

type FieldValues = {
  introductoryMessage: string;
  questionMessage: string;
  endingMessage: string;
};

export const SurveyCustomizationTextToSpeechView = (): JSX.Element => {
  const { settingsId, languageCode } = useAppPathParams(
    '/settings/satisfaction/phone/survey/ivr/:settingsId/:languageCode',
  );

  const navigate = useAppNavigate();

  const handleNavigateBackToSurvey = useCallback(() => {
    navigate({
      path: `/settings/satisfaction/phone/survey/:settingsId`,
      params: { path: { settingsId } },
    });
  }, [navigate, settingsId]);

  const language: Language | undefined = useMemo(
    () => languages.filter((l) => l.code == languageCode).shift(),
    [languageCode],
  );

  const notify = useNotifications();

  useEffect(() => {
    // If no language is defined, we don't allow to continue further with customizting the language settings
    if (!language) {
      handleNavigateBackToSurvey();
    }
  }, [handleNavigateBackToSurvey, language]);

  const { formState, control, handleSubmit, setValue, getValues, reset } =
    useForm<FieldValues>({
      defaultValues: {
        introductoryMessage: '',
        questionMessage: '',
        endingMessage: '',
      },
      mode: 'all',
    });

  const [textToSpeechCustomization, setTextToSpeechCustomization] =
    useState<Satisfaction.TextToSpeechRatingCustomization>();

  const [createRatingSettings] = ratingApi.useLazyCreateRatingSettingsQuery();

  const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();

  const [
    readRatingSettings,
    { ratingSettings, isSuccess: readRatingSettingsIsSuccess },
  ] = ratingApi.useLazyReadRatingSettingsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      ratingSettings: data?.data.rating_settings ?? [],
    }),
  });

  const [
    getSurveyRatingSettings,
    { data: surveyRatingSettings, isSuccess: surveySettingIsSuccess },
  ] = ratingApi.useLazyGetRatingSettingsQuery();

  useEffect(() => {
    if (readRatingSettingsIsSuccess && textToSpeechCustomization) {
      setValue(
        'introductoryMessage',
        textToSpeechCustomization?.introductory_message ?? '',
      );
      setValue(
        'endingMessage',
        textToSpeechCustomization?.ending_message ?? '',
      );
      if (
        textToSpeechCustomization.rating_questions?.length &&
        !textToSpeechCustomization.rating_questions?.includes(
          getValues('questionMessage'),
        )
      ) {
        setValue(
          'questionMessage',
          textToSpeechCustomization.rating_questions[0],
        );
      }

      reset({}, { keepValues: true });
    }
  }, [readRatingSettingsIsSuccess, textToSpeechCustomization]);

  const handleCreateOrUpdateLanguageSettings = useCallback(
    async (fieldValues: FieldValues) => {
      try {
        const textToSpeechCustomization: Satisfaction.TextToSpeechRatingCustomization =
          {
            introductory_message: fieldValues.introductoryMessage ?? '',
            rating_questions: [fieldValues.questionMessage ?? ''],
            ending_message: fieldValues.endingMessage ?? '',
          };

        // Checks the fetched language settings, if they already exited it performs an update, if it did not it creates one.
        if (ratingSettings.length) {
          const updateReq: Satisfaction.UpdateTextToSpeechCsatSettingsParams = {
            id: ratingSettings[0].id,
            customization: textToSpeechCustomization,
          };
          updateRatingSettings(updateReq);
        } else {
          await createRatingSettings({
            language: languageCode,
            level: 'language',
            parent_id: settingsId,
            survey_type: 'text_to_speech',
            customization: textToSpeechCustomization,
          });

          // After creating, we fetch the settings again, which means that next time the language
          // settings object will be present so it will perform an update instead of create
          fetchLanguageRatingSettings();
        }

        notify(
          {
            title: 'Success',
            text: `The survey customization has been saved.`,
            icon: 'done',
            time: 5000,
          },
          NotificationType.Success,
        );

        reset({}, { keepValues: true });
      } catch (error) {
        notify(
          {
            title: 'Error while saving the survey customization.',
          },
          NotificationType.Error,
        );
      }
    },
    [
      updateRatingSettings,
      createRatingSettings,
      surveyRatingSettings,
      ratingSettings,
    ],
  );

  /**
   * For some reason, casting CSATCustomization to
   * TextToSpeechRatingCustomization fails and returns an undefined object. This
   * is a workaround to convert the content back to Json and parse it back.
   */
  function withTextToSpeech(
    customization: Satisfaction.CSATCustomization,
    handleTextToSpeech: (
      t: Satisfaction.TextToSpeechRatingCustomization,
    ) => void,
  ): void | undefined {
    try {
      const textToSpeech: Satisfaction.TextToSpeechRatingCustomization =
        customization as Satisfaction.TextToSpeechRatingCustomization;
      return handleTextToSpeech(textToSpeech);
    } catch {
      return undefined;
    }
  }

  useEffect(() => {
    getSurveyRatingSettings({ ratingSettingsId: settingsId });
  }, [settingsId]);

  const fetchLanguageRatingSettings = useCallback(() => {
    readRatingSettings({
      language: languageCode,
      parentId: settingsId,
      level: 'language',
      surveyType: surveyRatingSettings?.data.survey_type ?? 'text_to_speech',
    });
  }, [readRatingSettings, surveySettingIsSuccess]);

  useEffect(() => {
    if (surveyRatingSettings?.data) {
      fetchLanguageRatingSettings();
    }
  }, [surveyRatingSettings]);

  useEffect(() => {
    if (
      readRatingSettingsIsSuccess &&
      ratingSettings &&
      ratingSettings.length > 0
    ) {
      const customization = ratingSettings[0].customization ?? undefined;

      withTextToSpeech(customization, (textToSpeech) => {
        setTextToSpeechCustomization(textToSpeech);

        setValue(
          'introductoryMessage',
          textToSpeech?.introductory_message ?? '',
        );
        setValue('endingMessage', textToSpeech?.ending_message ?? '');
        if (textToSpeech?.rating_questions?.length) {
          setValue('questionMessage', textToSpeech?.rating_questions[0]);
        }
      });
    }
  }, [ratingSettings, readRatingSettingsIsSuccess]);

  const handleValidSubmit: SubmitHandler<FieldValues> = async (fieldValues) => {
    reset({}, { keepValues: true });
    await handleCreateOrUpdateLanguageSettings(fieldValues);
  };

  return (
    <>
      <form noValidate tabIndex={-1} onSubmit={handleSubmit(handleValidSubmit)}>
        <Page.Section
          titleMeta={
            <AppLink
              path={`/settings/satisfaction/phone/survey/:settingsId`}
              params={{ path: { settingsId } }}
            >
              ← Back to language options
            </AppLink>
          }
          controls={
            <Button
              variant="primary"
              disabled={!(formState.isDirty && formState.isValid)}
              type="submit"
              onClick={noop}
            >
              Save
            </Button>
          }
        >
          <FlexBox flexDirection="row" gap="500">
            {surveyRatingSettings?.data?.survey_type && (
              <FlexBox flexDirection="column" gap="100">
                <Text.Paragraph variant="1" subdued>
                  Survey type
                </Text.Paragraph>

                <Text.Paragraph variant="1">
                  {Satisfaction.prettySurveyType(
                    surveyRatingSettings?.data.survey_type,
                  )}
                </Text.Paragraph>
              </FlexBox>
            )}
            <FlexBox flexDirection="column" gap="100">
              <Text.Paragraph variant="1" subdued>
                CSAT survey name
              </Text.Paragraph>

              {surveySettingIsSuccess && surveyRatingSettings?.data && (
                <Text.Paragraph variant="1">
                  {surveyRatingSettings?.data.name}
                </Text.Paragraph>
              )}
            </FlexBox>

            <FlexBox flexDirection="column" gap="100">
              <Text.Paragraph variant="1" subdued>
                Language
              </Text.Paragraph>

              <Text.Paragraph variant="1">{language?.label}</Text.Paragraph>
            </FlexBox>
          </FlexBox>
        </Page.Section>

        <Page.Section>
          <Text.Paragraph variant="2">
            All defined messages will be turned into audio and played in order
            at the end of the phone call. Remember to ask your customer to leave
            a rating from 1 to 5, being 1 the lowest and 5 the highest.
          </Text.Paragraph>
        </Page.Section>

        <Page.Section>
          <FormGrid>
            <FormField>
              <HookFormText
                name="introductoryMessage"
                label="Introductory message"
                control={control}
                noAlert={true}
                helpIcon={{
                  text: 'Plays at the end of the conversation, typically used to briefly introduce to the survey.',
                  dark: true,
                }}
                componentProps={{
                  placeholder: 'Write your message',
                }}
                rules={{
                  required: true,
                }}
                disabled={!readRatingSettingsIsSuccess}
              ></HookFormText>
            </FormField>

            <FormField>
              <HookFormText
                name="questionMessage"
                label="Question"
                control={control}
                noAlert={true}
                componentProps={{
                  placeholder: 'Write your question',
                }}
                rules={{
                  required: true,
                }}
                disabled={!readRatingSettingsIsSuccess}
              ></HookFormText>
            </FormField>

            <FormField>
              <HookFormText
                name="endingMessage"
                label="Ending message"
                control={control}
                noAlert={true}
                helpIcon={{
                  text: 'Plays after the end user responded, typically used to thank them.',
                  dark: true,
                }}
                componentProps={{
                  placeholder: 'Write your message',
                }}
                rules={{
                  required: true,
                }}
                disabled={!readRatingSettingsIsSuccess}
              ></HookFormText>
            </FormField>
          </FormGrid>
        </Page.Section>
      </form>
    </>
  );
};
